import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import routeConstants from 'routes/routeConstants';
import { selectEmployeePayrollTips } from 'store/Collections/EmployeePayrollTips';
import { selectSiteSettings } from 'store/Site';
import { getCalculatedTipsRequest, selectCalculatorPayees, setCalculatorPayees, setChangeLogEntries } from 'store/TipCalculator';
import { ICalculatorRules, IDaySegment, IPOSResults, ISite, ITipCalculatorChangeLogEntry, ITipCalculatorErrorPayload, ITipCalculatorResult } from 'types';
import { buildTipCalculatorPayeeFromPOSData, buildTipCalculatorRequests, calculateHoldbackAfterCash, processTipCalculatorError } from 'utils/tipCalculator';
import { IContentProps } from '../types';

interface IUseRecalculateWorkflowArgs {
  site: ISite;
  businessDate: string;
  tipCalcRules: ICalculatorRules;
  segments: IDaySegment[];
  posDataResults: IPOSResults;
  changeLogEntries?: ITipCalculatorChangeLogEntry[];
  onSuccess?: () => void;
}

export const useRecalculateWorkflow = ({ site, tipCalcRules, segments, posDataResults, changeLogEntries = [], onSuccess }: IUseRecalculateWorkflowArgs) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const currentCalcPayees = useSelector(selectCalculatorPayees());
  const payrollTips = useSelector(selectEmployeePayrollTips());
  const { timezone, tipCalculatorPercentFee: creditCardPercentFee } = site?.settings || {};
  const { holdback: currentSiteHoldbacks } = useSelector(selectSiteSettings()) || {};

  const contentPropsMap: Record<string, Partial<IContentProps>> = {
    calculateTips: {
      message: 'Hang tight, this may take a moment.',
      stepMessage: 'Calculating tips...',
      status: 'loading',
      buttonText: 'Review Payouts',
    },
    calculateSuccess: {
      message: 'All systems go!',
      stepMessage: 'Success!',
      status: 'success',
      buttonText: 'Review Payouts',
      onClick: () => history.push(routeConstants.private.makePaymentCalculatorReview),
    },
    calculateError: {
      stepMessage: 'An error has occurred calculating tips',
      status: 'error',
      buttonText: 'Try Again',
      onClick: () => calculateTips(),
    },
  };

  const [contentProps, setContentProps] = useState<Partial<IContentProps>>(contentPropsMap.calculateTips);

  const onTipCalcSuccess = (tipCalcResults: ITipCalculatorResult[]) => {
    try {
      let payees = buildTipCalculatorPayeeFromPOSData(tipCalcResults, posDataResults, site, payrollTips?.data);

      /**
       * because payees is a complete override of existing payees, we need to merge modified fields
       * from the currentCalcPayees into the new payees array
       */
      if (currentCalcPayees?.length) {
        const currentPayeesById = Object.fromEntries(currentCalcPayees.map((payee) => [payee.id, payee]));

        payees = payees.map((payee) => {
          const currentPayee = currentPayeesById[payee.id];
          if (!currentPayee) return payee;

          const holdbackAmount = calculateHoldbackAfterCash({ ...payee, cash: currentPayee.cash }, currentSiteHoldbacks, changeLogEntries);

          return {
            ...payee,
            note: currentPayee.note,
            employee: currentPayee.employee,
            cash: currentPayee.cash,
            net: payee.gross - holdbackAmount,
            holdbackAmount,
          };
        });
      }

      dispatch(setChangeLogEntries(changeLogEntries));
      dispatch(setCalculatorPayees(payees));
      onSuccess?.();

      setContentProps(contentPropsMap.calculateSuccess);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('error building tip calculator payee from labor ticket data', err);

      setContentProps({ ...contentPropsMap.calculateError, errors: [err] });
    }
  };

  const onTipCalcFailure = (errorPayload: ITipCalculatorErrorPayload) => {
    const processedError = processTipCalculatorError(errorPayload);
    setContentProps({ ...contentPropsMap.calculateError, errors: processedError });
  };

  const calculateTips = () => {
    const tipCalcRequests = buildTipCalculatorRequests(posDataResults, tipCalcRules, segments);
    setContentProps(contentPropsMap.calculateTips);
    dispatch(getCalculatedTipsRequest(tipCalcRequests, posDataResults, creditCardPercentFee, onTipCalcSuccess, onTipCalcFailure));
  };

  useEffect(() => {
    calculateTips();
  }, [site.id]);

  return { contentProps, timeZone: timezone };
};
